import { useCallback, useEffect, useState } from 'react';
import { Printer } from 'lucide-react';
import { api } from '../api/client';
import { naviguer } from '../App';
import { Montant } from '../components/Montant';
import { aujourdhui, badgeStatut, dateCourte, debutMois, LIBELLE_STATUT, moisPrecedent } from '../utils/format';
import type { Facture } from '../types';

// Toutes les factures/relevés, avec leur statut, et l'export comptable des paiements.
export function FacturesPage() {
  const [factures, setFactures] = useState<Facture[]>([]);
  const [statut, setStatut] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const prec = moisPrecedent();
  const [exportDu, setExportDu] = useState(debutMois());
  const [exportAu, setExportAu] = useState(aujourdhui());

  const charger = useCallback(() => {
    setLoading(true);
    api.getFactures(statut || undefined).then((f) => { setFactures(f); setError(null); })
      .catch((e) => setError(e instanceof Error ? e.message : 'Échec du chargement'))
      .finally(() => setLoading(false));
  }, [statut]);
  useEffect(charger, [charger]);

  const changer = async (f: Facture, patch: Parameters<typeof api.updateFacture>[1]) => {
    try { await api.updateFacture(f.id, patch); charger(); } catch (e) { setError(e instanceof Error ? e.message : 'Échec'); }
  };

  const chip = (s: string, label: string) => <button className={`chip ${statut === s ? 'active' : ''}`} onClick={() => setStatut(s)}>{label}</button>;

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-heading">
          <span className="page-title">Factures</span>
          <p className="page-meta">Relevés émis depuis l'app. « Payée » crédite le solde de la personne ; la facturation comptable reste chez Jenna.</p>
        </div>
      </div>

      <div className="toolbar">
        <div className="filter-chips">
          {chip('', 'Toutes')}{chip('brouillon', 'Brouillons')}{chip('envoyee', 'Envoyées')}{chip('payee', 'Payées')}{chip('annulee', 'Annulées')}
        </div>
        <form className="flex items-center gap-8 ml-auto" onSubmit={(e) => { e.preventDefault(); window.location.href = api.lienExportPaiements(exportDu, exportAu); }}>
          <span className="text-sm text-ink-3">Export comptable des paiements</span>
          <input type="date" className="form-input" value={exportDu} onChange={(e) => setExportDu(e.target.value)} style={{ width: 150 }} />
          <input type="date" className="form-input" value={exportAu} onChange={(e) => setExportAu(e.target.value)} style={{ width: 150 }} />
          <button className="btn btn-export" type="submit">CSV</button>
          <button className="btn btn-sm btn-ghost" type="button" onClick={() => { setExportDu(prec.du); setExportAu(prec.au); }}>mois précédent</button>
        </form>
      </div>

      {error && <div className="badge badge-err mb-12" style={{ display: 'block' }}>{error}</div>}

      {loading ? <div className="spinner" /> : factures.length === 0 ? (
        <div className="empty-state">Aucune facture{statut ? ` « ${LIBELLE_STATUT[statut]} »` : ''}. Une facture se crée depuis la fiche d'une personne.</div>
      ) : (
        <table className="table">
          <thead><tr><th>N°</th><th>Participant·e</th><th>Période</th><th className="num">Séances</th><th className="num">Montant</th><th>Statut</th><th>Émise le</th><th>Payée le</th><th></th></tr></thead>
          <tbody>
            {factures.map((f) => (
              <tr key={f.id}>
                <td className="text-mono text-sm">{f.numero}</td>
                <td className="font-medium is-clickable" style={{ cursor: 'pointer' }} onClick={() => naviguer('participants', f.participant_id)}>{f.participant_nom}</td>
                <td className="text-sm tabular-nums">{dateCourte(f.periode_du)} → {dateCourte(f.periode_au)}</td>
                <td className="num">{f.lignes.length}</td>
                <td className="num"><Montant valeur={f.montant} /></td>
                <td><span className={badgeStatut(f.statut)}>{LIBELLE_STATUT[f.statut]}</span></td>
                <td className="text-sm">{dateCourte(f.emise_le)}</td>
                <td className="text-sm">{dateCourte(f.payee_le)}</td>
                <td>
                  <div className="flex gap-4 justify-end">
                    {f.statut === 'brouillon' && <button className="btn btn-sm" onClick={() => changer(f, { statut: 'envoyee' })}>Envoyée</button>}
                    {f.statut === 'envoyee' && <button className="btn btn-sm btn-accent" onClick={() => changer(f, { statut: 'payee', payee_le: aujourdhui() })}>Payée</button>}
                    {f.statut === 'payee' && <button className="btn btn-sm btn-ghost" onClick={() => changer(f, { statut: 'envoyee' })}>Non payée</button>}
                    {f.statut !== 'annulee' && f.statut !== 'payee' && <button className="btn btn-sm btn-ghost" onClick={() => { if (confirm(`Annuler la facture ${f.numero} ?`)) changer(f, { statut: 'annulee' }); }}>Annuler</button>}
                    <button className="btn btn-sm btn-ghost" title="Imprimer / PDF" onClick={() => naviguer('factures', f.id, 'imprimer')}><Printer className="icon-sm" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
